import { Grid, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useBankings } from "../../services/bankingService";
import TableService from "../../services/tableService";

import { BANKING_TABLE_NAME, useBankingTableFieldsConfig } from "./BankingTableFieldsConfig";
import { AddBankingDialog } from "./AddBankingDialog";
import { BankingImportListDialog } from "./ImportBankingListDialog";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { centerVericalAlignIconStyle } from "../../helpers/styles";
import { InvestmentFilter } from "../../components/common/InvestmentFilter";

import BankingNavigationBar from "./BankingNavigationBar";

export default function FinancialPanelBankingPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.INVESTMENT_DPI, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT]);

    if (!hasAccess){
        return null
    }

    const { t } = useTranslation();
    const BANKING_TABLE_FIELDS_CONFIG = useBankingTableFieldsConfig();

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: bankingsData, isLoading } = useBankings(searchParams);

    useEffect(() => {
        if (bankingsData?.results) {
            setDataLocal(bankingsData.results);
            setRecordsCount(bankingsData.count);
        }
    }, [bankingsData, dataPage, dataPageSize]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    const banksDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            BANKING_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[BANKING_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[BANKING_TABLE_NAME] || [],
        );
    }, [BANKING_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                BANKING_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[BANKING_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[BANKING_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[BANKING_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[BANKING_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            BANKING_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    BANKING_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[BANKING_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[BANKING_TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"bankings/bankings/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={banksDataExportHeaders}
            exportToFileFileName={`${t("bankings.exportBankingsFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={false}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
        />
    );

      const { openDialog: openAddBankingDialog } = useDialogBetter();
      const { openDialog: openImportBankingDialog } = useDialogBetter(); 
    
      const handleOpenAddBankingDialog = (e) => {
        openAddBankingDialog({
          Component: AddBankingDialog,
        });
      };

    const handleOpenImportBankingDialog = (e) => {
        openImportBankingDialog({
          Component: BankingImportListDialog,
        });
      };

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.bankings")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12}>
                    < BankingNavigationBar />
                </Grid>
                <Grid item xs={2}>
                    <InvestmentFilter
                        filterName={"banking_invest"}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                    />
                </Grid>
                <Grid item xs={6}/>
                <Grid item xs={2}>
                    <Button fullWidth variant="contained" onClick={handleOpenAddBankingDialog}>
                        {t("bankings.add_banking")}
                    </Button>
                </Grid>
                <Grid item xs={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<FileUploadOutlinedIcon sx={centerVericalAlignIconStyle} />}
                        onClick={handleOpenImportBankingDialog}
                        sx={{bgcolor: "primary.light"}}
                    >
                        {t("bankings.importBanking")}
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>{tableComponent()}</LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
